// NewTenantWizard — 6-step dialog for creating a new tenant (Annexure A §5).
// Steps: 1 Org Info | 2 Plan & Type | 3 Compliance | 4 Contact | 5 Advanced | 6 Review

import { useState } from "react";
import type { JSX, FormEvent } from "react";
import { CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { createTenant } from "../lib/adminApiClient.js";

const STEPS = ["Org Info", "Plan & Type", "Compliance", "Contact", "Advanced", "Review"];
const INPUT = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

interface WizardState {
  name: string; city: string;
  plan: string; tenant_type: string;
  compliance_mode: string; nsofar_sync_enabled: boolean;
  billing_contact_email: string;
  kms_key_arn: string; ip_allowlist: string; whatsapp_id: string; exotel_id: string;
}

const INIT: WizardState = {
  name: "", city: "", plan: "standard", tenant_type: "standard",
  compliance_mode: "standard", nsofar_sync_enabled: false,
  billing_contact_email: "", kms_key_arn: "", ip_allowlist: "", whatsapp_id: "", exotel_id: "",
};

interface Props { onClose: () => void; onCreated: () => void; }

// eslint-disable-next-line max-lines-per-function, complexity, sonarjs/cognitive-complexity -- multi-step wizard component; splitting per-step hurts state cohesion
export function NewTenantWizard({ onClose, onCreated }: Props): JSX.Element {
  const [step, setStep] = useState(0);
  const [s, setS] = useState<WizardState>(INIT);
  const [saving, setSaving] = useState(false);
  const p = <K extends keyof WizardState>(k: K) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setS((prev) => ({ ...prev, [k]: e.target.value }));

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await createTenant({
        name: s.name.trim(), city: s.city.trim() || undefined, plan: s.plan,
        tenant_type: s.tenant_type, compliance_mode: s.compliance_mode,
        nsofar_sync_enabled: s.nsofar_sync_enabled,
        billing_contact_email: s.billing_contact_email.trim() || undefined,
      });
      toast.success(`Tenant "${s.name}" created.`);
      onCreated();
    } catch {
      toast.error("Failed to create tenant.");
    } finally {
      setSaving(false);
    }
  }

  const stepContent: JSX.Element[] = [
    // Step 0 — Org Info
    <div key="0" className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">Organisation Name *</label>
      <input value={s.name} onChange={p("name")} required maxLength={120} placeholder="Sunrise Care Services" className={INPUT} />
      <label className="block text-sm font-medium text-slate-700 mt-2">City</label>
      <input value={s.city} onChange={p("city")} maxLength={100} placeholder="Mumbai" className={INPUT} />
    </div>,
    // Step 1 — Plan & Type
    <div key="1" className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">Plan</label>
      <select value={s.plan} onChange={p("plan")} className={INPUT}>
        {["standard","pro","enterprise"].map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <label className="block text-sm font-medium text-slate-700 mt-2">Tenant Type</label>
      <select value={s.tenant_type} onChange={p("tenant_type")} className={INPUT}>
        <option value="standard">Operator</option>
        <option value="nsofar_partner">Nsofar Partner Agency</option>
        <option value="nsofar_internal">Nsofar Internal</option>
      </select>
    </div>,
    // Step 2 — Compliance
    <div key="2" className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">Compliance Mode</label>
      <select value={s.compliance_mode} onChange={p("compliance_mode")} className={INPUT}>
        <option value="standard">Standard</option>
        <option value="nsofar_compliance">Nsofar Compliance</option>
      </select>
      <div className="flex items-center gap-3 mt-2">
        <input id="nsofar" type="checkbox" checked={s.nsofar_sync_enabled}
          onChange={(e) => setS((prev) => ({ ...prev, nsofar_sync_enabled: e.target.checked }))}
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
        <label htmlFor="nsofar" className="text-sm font-medium text-slate-700">Enable Nsofar Sync</label>
      </div>
    </div>,
    // Step 3 — Contact
    <div key="3" className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">Billing Contact Email</label>
      <input type="email" value={s.billing_contact_email} onChange={p("billing_contact_email")} placeholder="billing@agency.com" className={INPUT} />
    </div>,
    // Step 4 — Advanced
    <div key="4" className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">KMS Key ARN</label>
      <input value={s.kms_key_arn} onChange={p("kms_key_arn")} placeholder="arn:aws:kms:..." className={INPUT} />
      <label className="block text-sm font-medium text-slate-700 mt-2">IP Allowlist (comma-separated)</label>
      <input value={s.ip_allowlist} onChange={p("ip_allowlist")} placeholder="1.2.3.4, 5.6.7.8" className={INPUT} />
      <label className="block text-sm font-medium text-slate-700 mt-2">WhatsApp Phone Number ID</label>
      <input value={s.whatsapp_id} onChange={p("whatsapp_id")} className={INPUT} />
      <label className="block text-sm font-medium text-slate-700 mt-2">Exotel Caller ID</label>
      <input value={s.exotel_id} onChange={p("exotel_id")} className={INPUT} />
    </div>,
    // Step 5 — Review
    <div key="5" className="space-y-2 text-sm">
      {[["Name", s.name],["City", s.city||"—"],["Plan", s.plan],["Type", s.tenant_type],
        ["Compliance", s.compliance_mode],["Nsofar Sync", s.nsofar_sync_enabled ? "Yes" : "No"],
        ["Billing Email", s.billing_contact_email||"—"]].map(([k, v]) => (
        <div key={k as string} className="flex justify-between py-1.5 border-b border-slate-100">
          <span className="font-medium text-slate-600">{k}</span>
          <span className="text-slate-800">{v as string}</span>
        </div>
      ))}
    </div>,
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">New Tenant</h2>
          <span className="text-xs text-slate-400">Step {step + 1} of {STEPS.length}</span>
        </div>
        {/* Step indicator */}
        <div className="flex gap-1.5">
          {STEPS.map((label, i) => (
            <div key={label} title={label} className={`flex-1 h-1.5 rounded-full transition-colors ${
              i < step ? "bg-indigo-400" : i === step ? "bg-indigo-600" : "bg-slate-200"
            }`} />
          ))}
        </div>
        <p className="text-sm font-medium text-slate-700">{STEPS[step]}</p>
        <form onSubmit={step < STEPS.length - 1 ? (e) => { e.preventDefault(); setStep((n) => n + 1); } : (e) => void submit(e)}
          className="space-y-4">
          {stepContent[step]}
          <div className="flex justify-between pt-2">
            <button type="button" onClick={step === 0 ? onClose : () => setStep((n) => n - 1)}
              className="px-4 py-2 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
              {step === 0 ? "Cancel" : "Back"}
            </button>
            <button type="submit" disabled={saving || (step === 0 && !s.name.trim())}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
              {step === STEPS.length - 1
                ? (saving ? "Creating…" : <><CheckCircle className="h-4 w-4" /> Create Tenant</>)
                : "Next →"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
